import dayjs from 'dayjs';

/**
 * Nghiệp vụ kho thẩm mỹ viện — hàm thuần (không đụng localStorage) để dễ test và sau này thay bằng API.
 * Tồn kho KHÔNG lưu trực tiếp: luôn tính từ các phiếu đã duyệt (sổ kho), nên không thể lệch với chứng từ.
 * Số lượng luôn lưu theo đơn vị nhỏ nhất (unit/ống/cái/shot…); đơn vị đóng gói chỉ dùng để nhập và hiển thị.
 */

export type ItemKind = 'drug' | 'consumable' | 'cosmetic' | 'tip' | 'tool';
export const ITEM_KIND: Record<ItemKind, { label: string; badge: string }> = {
  drug: { label: 'Thuốc / biệt dược', badge: 'bg-violet-50 text-violet-700 border-violet-200' },
  consumable: { label: 'Vật tư tiêu hao', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  cosmetic: { label: 'Mỹ phẩm bán lẻ', badge: 'bg-pink-50 text-pink-700 border-pink-200' },
  tip: { label: 'Đầu tip / cartridge', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  tool: { label: 'Công cụ dụng cụ', badge: 'bg-gray-100 text-gray-700 border-gray-200' },
};

export interface Item {
  id: string;
  code: string;
  name: string;
  kind: ItemKind;
  /** Đơn vị nhỏ nhất dùng để xuất, tính tồn (Unit, Ống, Cái, Shot…) */
  unit: string;
  /** Đơn vị đóng gói khi mua (Lọ, Hộp, Cartridge…) — 1 packUnit = packSize unit */
  packUnit?: string;
  packSize?: number;
  /** Tồn tối thiểu (theo unit, cộng mọi kho) — dưới mức này cảnh báo đặt hàng */
  minStock: number;
  /** Giá vốn mặc định / unit (khi chưa có phiếu nhập) */
  cost: number;
  /** Giá bán lẻ / unit (mỹ phẩm) */
  price?: number;
  /** Bảo quản lạnh 2–8°C */
  cold?: boolean;
  /** Thuốc kiểm soát: xuất phải có bác sĩ / điều dưỡng trưởng duyệt chuyên môn */
  controlled?: boolean;
  supplierId?: string;
  brand?: string;
  active: boolean;
  note?: string;
}

export type WarehouseKind = 'central' | 'branch' | 'room' | 'retail';
export const WAREHOUSE_KIND: Record<WarehouseKind, string> = {
  central: 'Kho tổng', branch: 'Kho chi nhánh', room: 'Tủ thuốc phòng thủ thuật', retail: 'Quầy mỹ phẩm',
};
export interface Warehouse { id: string; code: string; name: string; kind: WarehouseKind; branch: string; keeper: string }

export interface Supplier { id: string; name: string; phone: string }
export interface Service { id: string; name: string; price: number }
export interface NormLine { itemId: string; qty: number }

export type DocType = 'import' | 'issue_service' | 'issue_sale' | 'transfer' | 'dispose' | 'count';
export const DOC_TYPE: Record<DocType, { label: string; short: string; prefix: string; badge: string; out: boolean }> = {
  import: { label: 'Nhập mua', short: 'Nhập', prefix: 'PN', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', out: false },
  issue_service: { label: 'Xuất dùng dịch vụ', short: 'Xuất DV', prefix: 'PXD', badge: 'bg-sky-50 text-sky-700 border-sky-200', out: true },
  issue_sale: { label: 'Xuất bán', short: 'Xuất bán', prefix: 'PXB', badge: 'bg-pink-50 text-pink-700 border-pink-200', out: true },
  transfer: { label: 'Chuyển kho', short: 'Chuyển', prefix: 'PCK', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', out: true },
  dispose: { label: 'Xuất hủy', short: 'Hủy', prefix: 'PXH', badge: 'bg-rose-50 text-rose-700 border-rose-200', out: true },
  count: { label: 'Kiểm kho', short: 'Kiểm', prefix: 'PKK', badge: 'bg-amber-50 text-amber-700 border-amber-200', out: false },
};
export const OUT_TYPES: DocType[] = ['issue_service', 'issue_sale', 'transfer', 'dispose'];

export type DocStatus = 'draft' | 'pending' | 'approved' | 'rejected' | 'cancelled';
export const DOC_STATUS: Record<DocStatus, { label: string; badge: string }> = {
  draft: { label: 'Nháp', badge: 'bg-gray-100 text-gray-600 border-gray-200' },
  pending: { label: 'Chờ duyệt', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Đã duyệt', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Từ chối', badge: 'bg-rose-50 text-rose-700 border-rose-200' },
  cancelled: { label: 'Đã hủy', badge: 'bg-gray-100 text-gray-400 border-gray-200' },
};

export const DISPOSE_REASONS = ['Hết hạn sử dụng', 'Hỏng / vỡ', 'Sai nhiệt độ bảo quản', 'Mất / thiếu', 'Khác'];

export interface DocLine {
  id: string;
  itemId: string;
  lotNo: string;
  /** YYYY-MM-DD */
  expiry?: string;
  /** Số lượng theo unit (luôn dương; kiểm kho dùng systemQty/counted) */
  qty: number;
  /** Giá vốn / unit */
  unitCost: number;
  /** Nhập: SL trên chứng từ (để đối chiếu với SL thực nhận = qty) */
  docQty?: number;
  /** Kiểm kho: tồn sổ sách lúc kiểm và SL đếm thực tế */
  systemQty?: number;
  counted?: number;
}

export interface DocLog { at: number; by: string; text: string }

export interface StockDoc {
  id: string;
  code: string;
  type: DocType;
  status: DocStatus;
  /** Ngày chứng từ (ms) */
  date: number;
  warehouseId: string;
  /** Chuyển kho: kho nhận */
  toWarehouseId?: string;
  supplierId?: string;
  /** Số hóa đơn / đơn mua */
  refCode?: string;
  customer?: { name: string; phone: string };
  serviceId?: string;
  practitioner?: string;
  reason?: string;
  note?: string;
  lines: DocLine[];
  createdBy: string;
  createdAt: number;
  approvedBy?: string;
  approvedAt?: number;
  /** Người duyệt chuyên môn (thuốc kiểm soát) */
  expertBy?: string;
  receivedBy?: string;
  receivedAt?: number;
  rejectReason?: string;
  log: DocLog[];
}

export interface KhoState {
  v: 1;
  seededAt: number;
  /** Phiên bản bộ dữ liệu demo đã nạp (để tự làm mới khi demo được bổ sung) */
  demoVersion?: number;
  items: Item[];
  warehouses: Warehouse[];
  suppliers: Supplier[];
  services: Service[];
  norms: Record<string, NormLine[]>;
  docs: StockDoc[];
}

/** Ngưỡng cảnh báo cận hạn (ngày) */
export const NEAR_EXPIRY_DAYS = 60;
export const URGENT_EXPIRY_DAYS = 30;
export const NO_LOT = '—';

// ===================== Tiện ích =====================

const nf = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 });
export const fmtNum = (n: number) => nf.format(Math.round(n * 100) / 100);
export const fmtMoney = (n: number) => `${nf.format(Math.round(n))} ₫`;
export const itemById = (s: KhoState, id?: string) => s.items.find(i => i.id === id) || null;
export const whById = (s: KhoState, id?: string) => s.warehouses.find(w => w.id === id) || null;
export const supplierById = (s: KhoState, id?: string) => s.suppliers.find(x => x.id === id) || null;
export const serviceById = (s: KhoState, id?: string) => s.services.find(x => x.id === id) || null;
export const docById = (s: KhoState, id?: string | null) => s.docs.find(d => d.id === id) || null;

/** "250 Unit (2 Lọ + 50 Unit)" */
export function fmtQty(item: Pick<Item, 'unit' | 'packUnit' | 'packSize'> | null, q: number) {
  if (!item) return fmtNum(q);
  const base = `${fmtNum(q)} ${item.unit}`;
  const size = item.packSize || 1;
  if (!item.packUnit || size <= 1 || Math.abs(q) < size) return base;
  const packs = Math.trunc(q / size);
  const rest = Math.round((q - packs * size) * 100) / 100;
  return `${base} (${fmtNum(packs)} ${item.packUnit}${rest ? ` + ${fmtNum(rest)} ${item.unit}` : ''})`;
}

/** Số ngày còn lại tới hạn dùng (âm = đã hết hạn); null nếu không quản lý hạn */
export function daysToExpiry(expiry: string | undefined, now: number) {
  if (!expiry) return null;
  return dayjs(expiry).endOf('day').diff(dayjs(now).startOf('day'), 'day');
}
export type ExpiryLevel = 'expired' | 'urgent' | 'near' | 'ok' | 'none';
export function expiryLevel(expiry: string | undefined, now: number): ExpiryLevel {
  const d = daysToExpiry(expiry, now);
  if (d === null) return 'none';
  if (d < 0) return 'expired';
  if (d <= URGENT_EXPIRY_DAYS) return 'urgent';
  if (d <= NEAR_EXPIRY_DAYS) return 'near';
  return 'ok';
}

export const lineValue = (l: DocLine) => l.qty * l.unitCost;
export const docValue = (d: StockDoc) =>
  d.type === 'count' ? d.lines.reduce((t, l) => t + ((l.counted ?? 0) - (l.systemQty ?? 0)) * l.unitCost, 0) : d.lines.reduce((t, l) => t + lineValue(l), 0);
export const hasControlled = (s: KhoState, d: Pick<StockDoc, 'lines' | 'type'>) =>
  OUT_TYPES.includes(d.type) && d.type !== 'transfer' && d.lines.some(l => itemById(s, l.itemId)?.controlled);
/** Chuyển kho đã duyệt (hàng rời kho nguồn) nhưng kho nhận chưa xác nhận */
export const inTransit = (d: StockDoc) => d.type === 'transfer' && d.status === 'approved' && !d.receivedAt;

// ===================== Sổ kho =====================

export interface Move {
  docId: string;
  code: string;
  type: DocType;
  at: number;
  warehouseId: string;
  itemId: string;
  lotNo: string;
  expiry?: string;
  /** + nhập / − xuất (unit) */
  qty: number;
  unitCost: number;
  customer?: { name: string; phone: string };
  serviceId?: string;
  practitioner?: string;
  /** Kho đối ứng (chuyển kho) */
  otherWarehouseId?: string;
}

/** Bút toán kho sinh ra từ một phiếu (chỉ phiếu đã duyệt mới ghi sổ) */
export function movesOf(d: StockDoc): Move[] {
  if (d.status !== 'approved') return [];
  const base = (l: DocLine, warehouseId: string, qty: number, at: number, other?: string): Move => ({
    docId: d.id, code: d.code, type: d.type, at, warehouseId, itemId: l.itemId, lotNo: l.lotNo || NO_LOT, expiry: l.expiry, qty, unitCost: l.unitCost,
    customer: d.customer, serviceId: d.serviceId, practitioner: d.practitioner, otherWarehouseId: other,
  });
  const out: Move[] = [];
  for (const l of d.lines) {
    if (d.type === 'import') out.push(base(l, d.warehouseId, l.qty, d.date));
    else if (d.type === 'count') {
      const diff = (l.counted ?? 0) - (l.systemQty ?? 0);
      if (diff) out.push(base(l, d.warehouseId, diff, d.date));
    } else if (d.type === 'transfer') {
      out.push(base(l, d.warehouseId, -l.qty, d.approvedAt ?? d.date, d.toWarehouseId));
      if (d.receivedAt && d.toWarehouseId) out.push(base(l, d.toWarehouseId, l.qty, d.receivedAt, d.warehouseId));
    } else out.push(base(l, d.warehouseId, -l.qty, d.date));
  }
  return out;
}

const movesCache = new WeakMap<KhoState, Move[]>();
export function allMoves(s: KhoState): Move[] {
  let m = movesCache.get(s);
  if (!m) {
    m = s.docs.flatMap(movesOf).sort((a, b) => a.at - b.at);
    movesCache.set(s, m);
  }
  return m;
}

export interface Lot {
  key: string;
  warehouseId: string;
  itemId: string;
  lotNo: string;
  expiry?: string;
  qty: number;
  /** Giá vốn của lô (theo phiếu nhập gần nhất của lô) */
  unitCost: number;
}

/** Tồn theo kho × mặt hàng × lô tại thời điểm `at` (mặc định: hiện tại) */
export function lotsAt(s: KhoState, at = Infinity): Lot[] {
  const map = new Map<string, Lot>();
  for (const m of allMoves(s)) {
    if (m.at > at) break;
    const key = `${m.warehouseId}|${m.itemId}|${m.lotNo}`;
    const cur = map.get(key);
    if (cur) {
      cur.qty += m.qty;
      if (m.qty > 0 && m.unitCost) cur.unitCost = m.unitCost;
      if (!cur.expiry && m.expiry) cur.expiry = m.expiry;
    } else map.set(key, { key, warehouseId: m.warehouseId, itemId: m.itemId, lotNo: m.lotNo, expiry: m.expiry, qty: m.qty, unitCost: m.unitCost });
  }
  return Array.from(map.values()).map(l => ({ ...l, qty: Math.round(l.qty * 1000) / 1000 }));
}

const lotsCache = new WeakMap<KhoState, Lot[]>();
/** Các lô còn hàng hiện tại */
export function stockLots(s: KhoState): Lot[] {
  let l = lotsCache.get(s);
  if (!l) {
    l = lotsAt(s).filter(x => x.qty > 0);
    lotsCache.set(s, l);
  }
  return l;
}

export const byExpiry = (a: { expiry?: string }, b: { expiry?: string }) =>
  (a.expiry || '9999-12-31').localeCompare(b.expiry || '9999-12-31');

/** Lô còn hàng của một mặt hàng tại một kho, xếp hạn gần nhất trước (FEFO) */
export const lotsFor = (s: KhoState, warehouseId: string, itemId: string) =>
  stockLots(s).filter(l => l.warehouseId === warehouseId && l.itemId === itemId).sort(byExpiry);

export const qtyOf = (s: KhoState, itemId: string, warehouseId?: string) =>
  stockLots(s).filter(l => l.itemId === itemId && (!warehouseId || l.warehouseId === warehouseId)).reduce((t, l) => t + l.qty, 0);

/** Số lượng đang nằm trong phiếu xuất chờ duyệt (đã "giữ chỗ") */
export function reservedQty(s: KhoState, warehouseId: string, itemId: string, lotNo?: string, exceptDocId?: string) {
  return s.docs
    .filter(d => d.status === 'pending' && OUT_TYPES.includes(d.type) && d.warehouseId === warehouseId && d.id !== exceptDocId)
    .flatMap(d => d.lines)
    .filter(l => l.itemId === itemId && (lotNo === undefined || (l.lotNo || NO_LOT) === lotNo))
    .reduce((t, l) => t + l.qty, 0);
}

/** Giá vốn bình quân gia quyền / unit theo các phiếu nhập đã duyệt */
export function avgCost(s: KhoState, itemId: string) {
  let q = 0;
  let v = 0;
  for (const m of allMoves(s)) {
    if (m.itemId === itemId && m.type === 'import') { q += m.qty; v += m.qty * m.unitCost; }
  }
  return q > 0 ? v / q : itemById(s, itemId)?.cost ?? 0;
}

// ===================== FEFO & kiểm tra phiếu =====================

export interface Allocation { lines: Omit<DocLine, 'id'>[]; short: number }

/**
 * Chia số lượng cần xuất vào các lô theo FEFO (hạn gần nhất xuất trước).
 * Bỏ qua lô đã hết hạn trừ khi xuất hủy; trừ phần đang bị phiếu chờ duyệt khác giữ.
 */
export function allocateFefo(s: KhoState, warehouseId: string, itemId: string, qty: number, opts: { now: number; allowExpired?: boolean; exceptDocId?: string }): Allocation {
  const lines: Omit<DocLine, 'id'>[] = [];
  let need = qty;
  for (const lot of lotsFor(s, warehouseId, itemId)) {
    if (need <= 0) break;
    if (!opts.allowExpired && expiryLevel(lot.expiry, opts.now) === 'expired') continue;
    const free = lot.qty - reservedQty(s, warehouseId, itemId, lot.lotNo, opts.exceptDocId);
    if (free <= 0) continue;
    const take = Math.min(free, need);
    lines.push({ itemId, lotNo: lot.lotNo, expiry: lot.expiry, qty: take, unitCost: lot.unitCost || avgCost(s, itemId) });
    need -= take;
  }
  return { lines, short: Math.max(0, Math.round(need * 1000) / 1000) };
}

/** Lỗi chặn khi gửi duyệt / duyệt phiếu (rỗng = hợp lệ) */
export function validateDoc(s: KhoState, d: StockDoc, now: number): string[] {
  const errs: string[] = [];
  const name = (id: string) => itemById(s, id)?.name || 'Mặt hàng';
  if (!d.warehouseId) errs.push('Chưa chọn kho');
  if (!d.lines.length) errs.push('Phiếu chưa có mặt hàng');
  if (d.type === 'transfer') {
    if (!d.toWarehouseId) errs.push('Chưa chọn kho nhận');
    else if (d.toWarehouseId === d.warehouseId) errs.push('Kho nhận phải khác kho xuất');
  }
  if (d.type === 'import') {
    if (!d.supplierId) errs.push('Chưa chọn nhà cung cấp');
    for (const l of d.lines) {
      if (!(l.qty > 0)) errs.push(`${name(l.itemId)}: số lượng thực nhận phải > 0`);
      if (!l.lotNo || l.lotNo === NO_LOT) errs.push(`${name(l.itemId)}: thiếu số lô`);
      if (!l.expiry && itemById(s, l.itemId)?.kind !== 'tool') errs.push(`${name(l.itemId)}: thiếu hạn sử dụng`);
      else if (l.expiry && expiryLevel(l.expiry, now) === 'expired') errs.push(`${name(l.itemId)}: lô ${l.lotNo} đã hết hạn, không được nhập`);
    }
  }
  if (d.type === 'issue_service' && !d.serviceId) errs.push('Chưa chọn dịch vụ');
  // Ghi nhận khách để truy xuất được lô thuốc đã tiêm cho ai
  if (d.type === 'issue_service' && !d.customer?.name?.trim()) errs.push('Chưa nhập khách hàng (cần để truy xuất lô)');
  if (d.type === 'dispose' && !d.reason) errs.push('Chưa chọn lý do hủy');
  if (d.type === 'count') {
    for (const l of d.lines) if (l.counted === undefined || l.counted === null || Number.isNaN(l.counted) || l.counted < 0) errs.push(`${name(l.itemId)} (lô ${l.lotNo}): chưa nhập số đếm`);
  }
  if (OUT_TYPES.includes(d.type)) {
    // Gộp các dòng cùng lô rồi so với tồn hiện tại (trừ phần phiếu chờ duyệt khác đang giữ)
    const need = new Map<string, number>();
    for (const l of d.lines) {
      if (!(l.qty > 0)) errs.push(`${name(l.itemId)}: số lượng phải > 0`);
      const k = `${l.itemId}|${l.lotNo || NO_LOT}`;
      need.set(k, (need.get(k) || 0) + l.qty);
      if (d.type !== 'dispose' && expiryLevel(l.expiry, now) === 'expired') errs.push(`${name(l.itemId)}: lô ${l.lotNo} đã hết hạn — chỉ được xuất hủy`);
    }
    need.forEach((q, k) => {
      const [itemId, lotNo] = k.split('|');
      const have = lotsFor(s, d.warehouseId, itemId).find(l => l.lotNo === lotNo)?.qty ?? 0;
      const free = have - reservedQty(s, d.warehouseId, itemId, lotNo, d.id);
      if (q > free + 1e-9) errs.push(`${name(itemId)} lô ${lotNo}: cần ${fmtQty(itemById(s, itemId), q)}, chỉ còn ${fmtQty(itemById(s, itemId), Math.max(0, free))} có thể xuất`);
    });
  }
  return Array.from(new Set(errs));
}

/** Cảnh báo không chặn (để người duyệt cân nhắc) */
export function docWarnings(s: KhoState, d: StockDoc, now: number): string[] {
  const w: string[] = [];
  for (const l of d.lines) {
    const it = itemById(s, l.itemId);
    if (d.type === 'import' && l.docQty !== undefined && l.docQty !== l.qty)
      w.push(`${it?.name}: thực nhận ${fmtNum(l.qty)} khác chứng từ ${fmtNum(l.docQty)} (${l.qty > l.docQty ? '+' : ''}${fmtNum(l.qty - l.docQty)})`);
    const lv = expiryLevel(l.expiry, now);
    if (d.type === 'import' && (lv === 'urgent' || lv === 'near')) w.push(`${it?.name}: lô ${l.lotNo} hạn dùng ngắn (còn ${daysToExpiry(l.expiry, now)} ngày)`);
  }
  if (d.type === 'issue_service' && d.serviceId) {
    const v = normVariance(s, d);
    v.filter(x => x.actual > x.norm * 1.2 && x.norm > 0).forEach(x => w.push(`${itemById(s, x.itemId)?.name}: dùng ${fmtNum(x.actual)} vượt định mức ${fmtNum(x.norm)}`));
  }
  if (hasControlled(s, d)) w.push('Có thuốc kiểm soát — cần bác sĩ / điều dưỡng trưởng duyệt chuyên môn');
  return w;
}

/** So sánh lượng dùng thực tế của một phiếu xuất dịch vụ với định mức */
export function normVariance(s: KhoState, d: Pick<StockDoc, 'serviceId' | 'lines'>) {
  const norm = (d.serviceId && s.norms[d.serviceId]) || [];
  const ids = Array.from(new Set([...norm.map(n => n.itemId), ...d.lines.map(l => l.itemId)]));
  return ids.map(itemId => ({
    itemId,
    norm: norm.find(n => n.itemId === itemId)?.qty ?? 0,
    actual: d.lines.filter(l => l.itemId === itemId).reduce((t, l) => t + l.qty, 0),
  }));
}

// ===================== Tổng hợp =====================

export interface ItemStock {
  item: Item;
  qty: number;
  value: number;
  lots: Lot[];
  nearestExpiry?: string;
  low: boolean;
  expiry: ExpiryLevel;
}

/** Tồn theo mặt hàng (lọc theo kho nếu có). Cờ "sắp hết" luôn so trên tổng mọi kho. */
export function itemStocks(s: KhoState, now: number, warehouseId?: string): ItemStock[] {
  const lots = stockLots(s);
  return s.items.map(item => {
    const mine = lots.filter(l => l.itemId === item.id && (!warehouseId || l.warehouseId === warehouseId)).sort(byExpiry);
    const qty = mine.reduce((t, l) => t + l.qty, 0);
    const total = warehouseId ? lots.filter(l => l.itemId === item.id).reduce((t, l) => t + l.qty, 0) : qty;
    const nearest = mine.find(l => l.expiry)?.expiry;
    const worst = (['expired', 'urgent', 'near'] as ExpiryLevel[]).find(lv => mine.some(l => expiryLevel(l.expiry, now) === lv)) || (nearest ? 'ok' : 'none');
    return {
      item, qty, lots: mine, nearestExpiry: nearest, expiry: worst,
      value: mine.reduce((t, l) => t + l.qty * (l.unitCost || item.cost), 0),
      low: item.active && item.minStock > 0 && total < item.minStock,
    };
  });
}

export interface ExpiryAlert extends Lot { days: number; level: ExpiryLevel; value: number }
export function expiryAlerts(s: KhoState, now: number): ExpiryAlert[] {
  return stockLots(s)
    .map(l => ({ ...l, days: daysToExpiry(l.expiry, now) ?? Infinity, level: expiryLevel(l.expiry, now), value: l.qty * l.unitCost }))
    .filter(l => l.level === 'expired' || l.level === 'urgent' || l.level === 'near')
    .sort((a, b) => a.days - b.days);
}

/** Đề xuất số lượng đặt mua: đưa tồn về 2× mức tối thiểu, làm tròn lên theo đơn vị đóng gói */
export function reorderQty(item: Item, current: number) {
  const size = item.packSize || 1;
  const need = Math.max(item.minStock * 2 - current, size);
  return Math.ceil(need / size) * size;
}

export interface XntRow { item: Item; opening: number; inQty: number; outQty: number; closing: number; inValue: number; outValue: number; closingValue: number }

/** Báo cáo xuất – nhập – tồn trong khoảng [from, to] */
export function xntReport(s: KhoState, from: number, to: number, warehouseId?: string): XntRow[] {
  const rows = new Map<string, XntRow>();
  const row = (itemId: string) => {
    let r = rows.get(itemId);
    if (!r) {
      const item = itemById(s, itemId);
      if (!item) return null;
      r = { item, opening: 0, inQty: 0, outQty: 0, closing: 0, inValue: 0, outValue: 0, closingValue: 0 };
      rows.set(itemId, r);
    }
    return r;
  };
  for (const m of allMoves(s)) {
    if (m.at > to) break;
    if (warehouseId && m.warehouseId !== warehouseId) continue;
    const r = row(m.itemId);
    if (!r) continue;
    if (m.at < from) r.opening += m.qty;
    else if (m.qty > 0) { r.inQty += m.qty; r.inValue += m.qty * m.unitCost; }
    else { r.outQty -= m.qty; r.outValue -= m.qty * m.unitCost; }
  }
  return Array.from(rows.values())
    .map(r => {
      const closing = r.opening + r.inQty - r.outQty;
      return { ...r, closing, closingValue: closing * avgCost(s, r.item.id) };
    })
    .filter(r => r.opening || r.inQty || r.outQty || r.closing)
    .sort((a, b) => a.item.code.localeCompare(b.item.code));
}

export interface LedgerRow extends Move { balance: number }
/** Thẻ kho của một mặt hàng: từng phát sinh kèm tồn lũy kế */
export function ledger(s: KhoState, itemId: string, warehouseId?: string): LedgerRow[] {
  let bal = 0;
  return allMoves(s)
    .filter(m => m.itemId === itemId && (!warehouseId || m.warehouseId === warehouseId))
    .map(m => { bal += m.qty; return { ...m, balance: Math.round(bal * 1000) / 1000 }; });
}

export interface ServiceUsage {
  service: Service;
  sessions: number;
  normCost: number;
  actualCost: number;
  items: { itemId: string; norm: number; actual: number; cost: number }[];
  byPractitioner: { name: string; sessions: number; normCost: number; actualCost: number }[];
}

/** Tiêu hao vật tư theo dịch vụ: thực tế so với định mức × số buổi */
export function serviceUsage(s: KhoState, from: number, to: number): ServiceUsage[] {
  const docs = s.docs.filter(d => d.type === 'issue_service' && d.status === 'approved' && d.serviceId && d.date >= from && d.date <= to);
  const unitCost = (id: string) => avgCost(s, id);
  return s.services
    .map(service => {
      const mine = docs.filter(d => d.serviceId === service.id);
      const norm = s.norms[service.id] || [];
      const normPerSession = norm.reduce((t, n) => t + n.qty * unitCost(n.itemId), 0);
      const ids = Array.from(new Set([...norm.map(n => n.itemId), ...mine.flatMap(d => d.lines.map(l => l.itemId))]));
      const items = ids.map(itemId => {
        const actual = mine.flatMap(d => d.lines).filter(l => l.itemId === itemId).reduce((t, l) => t + l.qty, 0);
        return { itemId, norm: (norm.find(n => n.itemId === itemId)?.qty ?? 0) * mine.length, actual, cost: actual * unitCost(itemId) };
      });
      const names = Array.from(new Set(mine.map(d => d.practitioner || 'Không rõ')));
      const byPractitioner = names.map(name => {
        const ds = mine.filter(d => (d.practitioner || 'Không rõ') === name);
        return { name, sessions: ds.length, normCost: normPerSession * ds.length, actualCost: ds.reduce((t, d) => t + d.lines.reduce((a, l) => a + l.qty * unitCost(l.itemId), 0), 0) };
      });
      return { service, sessions: mine.length, normCost: normPerSession * mine.length, actualCost: items.reduce((t, i) => t + i.cost, 0), items, byPractitioner };
    })
    .filter(u => u.sessions > 0)
    .sort((a, b) => b.actualCost - a.actualCost);
}

/** Chi phí vật tư định mức cho 1 buổi dịch vụ */
export const normCost = (s: KhoState, serviceId: string) => (s.norms[serviceId] || []).reduce((t, n) => t + n.qty * avgCost(s, n.itemId), 0);

/** Truy xuất lô: lô đã dùng cho khách nào, còn nằm ở kho nào */
export function traceLot(s: KhoState, q: string) {
  const k = q.trim().toLowerCase();
  if (!k) return { used: [] as Move[], stock: [] as Lot[] };
  const match = (itemId: string, lotNo: string) => {
    const it = itemById(s, itemId);
    return lotNo.toLowerCase().includes(k) || !!it?.name.toLowerCase().includes(k) || !!it?.code.toLowerCase().includes(k);
  };
  return {
    used: allMoves(s).filter(m => (m.type === 'issue_service' || m.type === 'issue_sale') && match(m.itemId, m.lotNo)).reverse(),
    stock: stockLots(s).filter(l => match(l.itemId, l.lotNo)).sort(byExpiry),
  };
}

/** Mã phiếu tiếp theo: PN_2610_003 */
export function nextCode(s: KhoState, type: DocType, at: number) {
  const prefix = `${DOC_TYPE[type].prefix}_${dayjs(at).format('YYMM')}_`;
  const n = s.docs.filter(d => d.code.startsWith(prefix)).map(d => parseInt(d.code.slice(prefix.length), 10) || 0);
  return `${prefix}${String((n.length ? Math.max(...n) : 0) + 1).padStart(3, '0')}`;
}

/** Danh sách lô trong kho để lập phiếu kiểm (tồn sổ sách hiện tại) */
export function countSheet(s: KhoState, warehouseId: string): Omit<DocLine, 'id'>[] {
  return stockLots(s)
    .filter(l => l.warehouseId === warehouseId)
    .sort((a, b) => (itemById(s, a.itemId)?.code || '').localeCompare(itemById(s, b.itemId)?.code || '') || byExpiry(a, b))
    .map(l => ({ itemId: l.itemId, lotNo: l.lotNo, expiry: l.expiry, qty: 0, unitCost: l.unitCost || avgCost(s, l.itemId), systemQty: l.qty }));
}
